import graphqlClient from "./graphqlClient";
import {
  ALL_CUSTOM_CLIPS,
  CLIP_DETAILS,
  RULE,
  RULES,
  USER_CLIPS,
  USER_CUSTOM_CLIPS,
  VALIDATE_VIDEO_CREATION_OPTIONS,
  VIDEO_CREATION_OPTIONS,
  WORKFLOW_DETAILS
} from "./graphql/sporty-queries";
import {
  DELETE_RULE,
  DELETE_USER_CUSTOM_CLIP,
  PUT_RULE,
  PUT_USER_CLIP,
  SAVE_ISSUE,
  SAVE_USER_CUSTOM_CLIP
} from "./graphql/sporty-mutations";
import { CROP_RATIOS } from "./ClippyApi";

export default class SportyApi {
  getRule(ruleId) {
    return graphqlClient
      .query({ query: RULE, variables: { ruleId } })
      .then(r => r.data && r.data.rule);
  }

  getRules(limit = 10, page = 0, allRules) {
    return graphqlClient
      .query({ query: RULES, variables: { limit, page, allRules } })
      .then(r => r.data && r.data.rules);
  }

  putRule(rule) {
    return graphqlClient
      .mutate({
        variables: { rule },
        mutation: PUT_RULE
      })
      .then(r => r.data && r.data.putRule.ruleId);
  }

  deleteRule(ruleId, limit, page, allRules) {
    return graphqlClient
      .mutate({
        variables: { ruleId },
        mutation: DELETE_RULE,
        refetchQueries: () => [
          { query: RULES, variables: { limit, page, allRules } }
        ],
        awaitRefetchQueries: true
      })
      .then(() => {
        return ruleId;
      });
  }

  getWorkflowDetails() {
    return graphqlClient.query({ query: WORKFLOW_DETAILS }).then(r => {
      return r;
    });
  }

  getVideoCreationOptions() {
    return graphqlClient.query({ query: VIDEO_CREATION_OPTIONS }).then(r => {
      return r;
    });
  }

  validateVideoCreationOptions(ruleFrequency, options, orderBy) {
    const videoCreationSelectionsInput = {
      orderingType: orderBy !== "NONE" ? orderBy : null,
      ruleFrequency: ruleFrequency,
      videoCreationOptions: options
    };

    return graphqlClient
      .query({
        query: VALIDATE_VIDEO_CREATION_OPTIONS,
        variables: { videoCreationSelections: videoCreationSelectionsInput }
      })
      .then(r => r.data && r.data.validateVideoCreationOptions.errors);
  }

  saveIssue(issue) {
    return graphqlClient
      .mutate({
        variables: { issue },
        mutation: SAVE_ISSUE
      })
      .then(r => r.data && r.data.saveIssue);
  }

  putUserClip({
    id,
    title,
    clips,
    applyCrossfade = false,
    addWatermark = false,
    cropRatio = "None",
    videoUri,
    thumbnailUri,
    width,
    height,
    username,
    duration,
    searchCategory
  }) {
    const options = [];
    const plays = [];
    const videos = [];

    clips.forEach((clip, i) => {
      if (clip.type === "DIRECT") {
        videos.push({
          url: clip.url,
          startOffset: clip.startOffset,
          endOffset: clip.endOffset,
          clipPosition: i
        });
      } else if (clip.type === "PLAY") {
        plays.push({
          playId: clip.playId,
          feedType: clip.feedType,
          startOffset: clip.startOffset,
          endOffset: clip.endOffset,
          clipPosition: i
        });
      } else {
        console.warn(`Got an unknown type of clip ${clip.type}, not linking`);
      }
    });

    if (applyCrossfade) {
      options.push("CROSS_FADE");
    }

    if (addWatermark) {
      options.push("ADD_MLB_WATERMARK");
    }

    if (cropRatio && cropRatio !== "None") {
      options.push(CROP_RATIOS[cropRatio]);
    }

    const userClip = {
      username,
      id,
      title,
      options,
      plays,
      videos,
      videoUri,
      thumbnailUri,
      width,
      height,
      duration,
      searchCategory
    };

    return graphqlClient
      .mutate({
        variables: { userClip },
        mutation: PUT_USER_CLIP
      })
      .then(r => r.data && r.data.putUserClip);
  }

  getUserClips(variables) {
    const defaultVariables = {
      page: 0,
      limit: 5,
      sortBy: null,
      title: null
    };
    return graphqlClient
      .query({
        fetchPolicy: "no-cache",
        query: USER_CLIPS,
        variables: { ...defaultVariables, ...variables }
      })
      .then(r => r.data && r.data.userClips);
  }

  getUserCustomClips(variables) {
    const defaultVariables = {
      limit: 5,
      page: 0,
      sortOrder: "CREATED_AT",
      description: null,
      gamePk: null
    };

    // When a user creates a clip we re-query the latest clips so we want to override
    // the cache to make sure the new clip shows up
    return graphqlClient
      .query({
        fetchPolicy: "no-cache",
        query: USER_CUSTOM_CLIPS,
        variables: { ...defaultVariables, ...variables }
      })
      .then(r => r.data && r.data.userCustomClips);
  }

  getAllCustomClips(variables) {
    const defaultVariables = {
      limit: 5,
      page: 0,
      sortOrder: "CREATED_AT",
      description: null,
      gamePk: null
    };

    return graphqlClient
      .query({
        fetchPolicy: "no-cache",
        query: ALL_CUSTOM_CLIPS,
        variables: { ...defaultVariables, ...variables }
      })
      .then(r => r.data && r.data.allUserCustomClips);
  }

  getClipInfo(clipId) {
    return graphqlClient
      .query({
        query: CLIP_DETAILS,
        variables: { clipId: clipId }
      })
      .then(r => r.data && r.data.clipInfo);
  }

  saveUserCustomClip(clip) {
    return graphqlClient
      .mutate({
        variables: { userCustomClip: clip },
        mutation: SAVE_USER_CUSTOM_CLIP
      })
      .then(r => r.data && r.data.saveUserCustomClip);
  }

  deleteUserCustomClip(clipId) {
    return graphqlClient
      .mutate({
        variables: { clipId },
        mutation: DELETE_USER_CUSTOM_CLIP
      })
      .then(r => r.data && r.data.deleteUserCustomClip);
  }
}



// WEBPACK FOOTER //
// ./src/httpClients/SportyApi.js